import React, { useEffect, useRef, useState } from "react";
import aimLogo from "../assets/aim.jpg";
import indiaLogo from "../assets/india.png";
import { School } from "lucide-react";

export default function LandingIntro({ onExplore }) {
  const canvasRef = useRef(null);
  const zoneRef = useRef(null);
  const rafRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: -9999, y: -9999, radius: 100 });
  const readyRef = useRef(false);
  const [showTech, setShowTech] = useState(false);

  const pulseDotRef = useRef({
    active: false,
    x: 0,
    y: 0,
    progress: 0,
    startTime: 0,
    duration: 2600,
    pause: 10000,
    lastTrigger: 0,
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
    radius: 34,
    force: 1.35,
  });

  useEffect(() => {
    const techTimer = setTimeout(() => setShowTech(true), 850);
    return () => clearTimeout(techTimer);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    let W = window.innerWidth;
    let H = window.innerHeight;

    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.scale(dpr, dpr);

    let metrics = null;

    // Logo-inspired blue → cyan → ice blue → aqua-green palette
    const gradColors = [
      [33, 99, 171],   // strong corporate blue
      [28, 154, 214],  // cyan-blue
      [132, 220, 255], // bright icy blue
      [67, 217, 196],  // soft aqua-green
    ];

    function lerp(a, b, t) {
      return a + (b - a) * t;
    }

    function easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function getColor(px, py, currentMetrics) {
      if (!currentMetrics) return `rgb(33, 99, 171)`;
      // Localize gradient to the word "BALVION" boundary
      const t = Math.max(0, Math.min(1, (px - currentMetrics.leftX) / currentMetrics.textWidth));
      const i = Math.floor(t * (gradColors.length - 1));
      const frac = t * (gradColors.length - 1) - i;
      const c1 = gradColors[Math.min(i, gradColors.length - 1)];
      const c2 = gradColors[Math.min(i + 1, gradColors.length - 1)];

      return `rgb(${Math.round(lerp(c1[0], c2[0], frac))}, ${Math.round(
        lerp(c1[1], c2[1], frac)
      )}, ${Math.round(lerp(c1[2], c2[2], frac))})`;
    }

    function getTextMetrics() {
      const zone = zoneRef.current;
      if (!zone) return null;

      const rect = zone.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2 - 10;
      // Increase font size on mobile to decrease gap
      const fontSize = Math.min(Math.max(W * 0.17, 64), 146);
      const text = "BALVION";

      const off = document.createElement("canvas");
      const offCtx = off.getContext("2d");
      offCtx.font = `800 ${fontSize}px Orbitron, Arial`;
      const metricsData = offCtx.measureText(text);
      const textWidth = metricsData.width;

      return {
        cx,
        cy,
        fontSize,
        text,
        textWidth,
        leftX: cx - textWidth / 2,
        rightX: cx + textWidth / 2,
      };
    }

    function buildParticles() {
      metrics = getTextMetrics();
      if (!metrics) return;

      const off = document.createElement("canvas");
      off.width = W;
      off.height = H;
      const offCtx = off.getContext("2d");

      offCtx.clearRect(0, 0, W, H);
      offCtx.fillStyle = "#1d7cc3";
      offCtx.textAlign = "center";
      offCtx.textBaseline = "middle";
      offCtx.font = `800 ${metrics.fontSize}px Orbitron, Arial`;
      offCtx.fillText(metrics.text, metrics.cx, metrics.cy);

      const imageData = offCtx.getImageData(0, 0, W, H).data;
      // Use higher particle density (smaller gap) on mobile to render smoothly and look high quality
      const gap = W < 640 ? 2 : Math.max(3, Math.round(W / 220));
      const pts = [];

      for (let y = 0; y < H; y += gap) {
        for (let x = 0; x < W; x += gap) {
          const idx = (y * W + x) * 4;
          if (imageData[idx + 3] > 128) {
            pts.push({
              x: Math.random() * W,
              y: Math.random() * H,
              baseX: x,
              baseY: y,
              vx: 0,
              vy: 0,
              // Slightly larger size scale on mobile for premium density feel
              size: gap * (W < 640 ? 0.62 : 0.48),
            });
          }
        }
      }

      particlesRef.current = pts;
      readyRef.current = true;

      const bX = metrics.leftX + metrics.textWidth * 0.045;
      const nX = metrics.rightX - metrics.textWidth * 0.04;
      const travelY = metrics.cy;

      Object.assign(pulseDotRef.current, {
        startX: bX,
        startY: travelY,
        endX: nX,
        endY: travelY,
        x: bX,
        y: travelY,
        lastTrigger: performance.now() + 1200,
      });
    }

    function updatePulseDot(now) {
      const pulse = pulseDotRef.current;

      if (!pulse.active && now - pulse.lastTrigger >= pulse.pause) {
        pulse.active = true;
        pulse.startTime = now;
        pulse.progress = 0;
        pulse.x = pulse.startX;
        pulse.y = pulse.startY;
      }

      if (pulse.active) {
        const raw = (now - pulse.startTime) / pulse.duration;
        const t = Math.min(raw, 1);
        const eased = easeInOutCubic(t);

        pulse.progress = eased;
        pulse.x = lerp(pulse.startX, pulse.endX, eased);
        pulse.y = lerp(pulse.startY, pulse.endY, eased);

        if (t >= 1) {
          pulse.active = false;
          pulse.lastTrigger = now;
        }
      }
    }

    function applyForce(sourceX, sourceY, radius, strength) {
      particlesRef.current.forEach((p) => {
        const dx = sourceX - p.x;
        const dy = sourceY - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < radius) {
          const angle = Math.atan2(dy, dx);
          const force = ((radius - dist) / radius) ** 2 * strength;
          p.vx -= Math.cos(angle) * force;
          p.vy -= Math.sin(angle) * force;
        }
      });
    }

    function drawPulseDot() {
      const pulse = pulseDotRef.current;
      if (!pulse.active) return;

      ctx.beginPath();
      ctx.arc(pulse.x, pulse.y, 4.5, 0, Math.PI * 2);
      ctx.fillStyle = "#aef7ff";
      ctx.shadowColor = "rgba(72, 214, 255, 0.9)";
      ctx.shadowBlur = 24;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(pulse.x, pulse.y, 11, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(67, 217, 196, 0.28)";
      ctx.lineWidth = 1.2;
      ctx.stroke();

      ctx.shadowBlur = 0;
    }

    function animate(now = 0) {
      ctx.clearRect(0, 0, W, H);

      if (readyRef.current) {
        const { x: mx, y: my, radius } = mouseRef.current;

        if (mx > -9000 && my > -9000) {
          applyForce(mx, my, radius, 3.5);
        }

        updatePulseDot(now);

        const pulse = pulseDotRef.current;
        if (pulse.active) {
          applyForce(pulse.x, pulse.y, pulse.radius, pulse.force);
        }

        particlesRef.current.forEach((p) => {
          p.vx += (p.baseX - p.x) * 0.038;
          p.vy += (p.baseY - p.y) * 0.038;

          p.vx *= 0.88;
          p.vy *= 0.88;

          p.x += p.vx;
          p.y += p.vy;

          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = getColor(p.baseX, p.baseY, metrics);
          ctx.fill();
        });

        drawPulseDot();
      }

      rafRef.current = requestAnimationFrame(animate);
    }

    const init = () => {
      buildParticles();
      animate();
    };

    // Wait for the fonts to be ready before drawing to avoid mismatched fallback font sizes
    if (document.fonts) {
      document.fonts.ready.then(init);
    } else {
      setTimeout(init, 200);
    }

    const onMouseMove = (e) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };

    const onMouseLeave = () => {
      mouseRef.current.x = -9999;
      mouseRef.current.y = -9999;
    };

    const onResize = () => {
      cancelAnimationFrame(rafRef.current);
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.scale(dpr, dpr);
      buildParticles();
      animate();
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseleave", onMouseLeave);
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <>
      <div
        className="relative min-h-screen overflow-hidden text-slate-800"
        style={{
          background:
            "linear-gradient(145deg, #eef7ff 0%, #e1f3ff 24%, #ffffff 58%, #f2fbff 100%)",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute rounded-full blur-3xl opacity-40"
            style={{
              width: "70vw",
              height: "70vw",
              top: "-20vw",
              left: "50%",
              transform: "translateX(-50%)",
              background:
                "radial-gradient(circle, rgba(88,197,255,0.45) 0%, rgba(157,245,255,0.28) 42%, rgba(86,214,188,0.16) 58%, transparent 72%)",
            }}
          />
          <div
            className="absolute rounded-full blur-2xl opacity-25"
            style={{
              width: "40vw",
              height: "40vw",
              bottom: "5vw",
              right: "-8vw",
              background:
                "radial-gradient(circle, rgba(125,211,252,0.38) 0%, transparent 65%)",
            }}
          />
          <div
            className="absolute rounded-full blur-2xl opacity-20"
            style={{
              width: "30vw",
              height: "30vw",
              bottom: "10vw",
              left: "-5vw",
              background:
                "radial-gradient(circle, rgba(125,255,229,0.26) 0%, transparent 65%)",
            }}
          />
        </div>

        <div
          className="absolute inset-0 pointer-events-none opacity-[0.10]"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(69,149,217,0.85) 1px, transparent 1px)",
            backgroundSize: "36px 36px",
          }}
        />

        <canvas
          ref={canvasRef}
          className="absolute inset-0 z-10 pointer-events-none"
        />

        <div className="relative z-20 flex flex-col items-center justify-center min-h-screen px-6 text-center pt-24 md:pt-16">
          <div
            className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[0.68rem] sm:text-[0.75rem] md:text-xs font-semibold uppercase tracking-[0.12em] sm:tracking-[0.22em] mb-3 text-center max-w-full"
            style={{
              borderColor: "rgba(56, 189, 248, 0.22)",
              background: "rgba(255,255,255,0.68)",
              color: "#0b76b7",
              backdropFilter: "blur(10px)",
              animation: "fadeUp 0.8s 0.2s both",
              boxShadow: "0 8px 30px rgba(56, 189, 248, 0.08)",
            }}
          >
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{
                background: "linear-gradient(135deg,#21a1d8,#6ee7f9,#43d9c4)",
              }}
            />
            Innovation · Education · Talent
          </div>

          <div
            ref={zoneRef}
            className="w-full"
            style={{ height: "clamp(110px, 16vw, 190px)" }}
          />

          <div
            className={`mt-[-4px] transition-all duration-1000 ${
              showTech ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
            }`}
            style={{
              fontFamily: "Orbitron, sans-serif",
              fontWeight: 700,
              fontSize: "clamp(1.15rem, 2.2vw, 1.55rem)",
              letterSpacing: showTech ? "0.42em" : "0.12em",
              textTransform: "uppercase",
              color: "#1678b8",
              textShadow: "0 1px 0 rgba(255,255,255,0.7)",
              transition:
                "letter-spacing 1.15s cubic-bezier(0.16,1,0.3,1), opacity 0.9s ease, transform 0.9s ease",
              paddingLeft: "0.42em",
            }}
          >
            Technologies
          </div>

          <p
            className="text-[0.95rem] md:text-[1.08rem] font-normal max-w-lg leading-[1.85] mt-5"
            style={{
              color: "#334155",
              animation: "fadeUp 0.9s 0.5s both",
            }}
          >
            Building a modern bridge between robotics education,
            <br className="hidden md:block" />
            talent development, and career transformation.
          </p>

          <button
            onClick={onExplore}
            className="group mt-9 inline-flex items-center gap-2.5 rounded-full text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, #0e7fc1 0%, #17a3c8 58%, #27b89b 100%)",
              color: "#ffffff",
              padding: "14px 32px",
              boxShadow:
                "0 10px 34px rgba(28,154,214,0.28), 0 1px 0 rgba(255,255,255,0.18) inset",
              animation: "fadeUp 0.9s 0.75s both",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                "0 14px 42px rgba(28,154,214,0.34), 0 1px 0 rgba(255,255,255,0.18) inset";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow =
                "0 10px 34px rgba(28,154,214,0.28), 0 1px 0 rgba(255,255,255,0.18) inset";
            }}
          >
            Get started
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="transition-transform duration-300 group-hover:translate-x-1"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>

          <div
            className="mt-10 flex flex-col items-center gap-5 md:flex-row md:gap-8 flex-wrap justify-center w-full mx-auto"
            style={{ animation: "fadeUp 0.9s 0.95s both" }}
          >
            {[
              { label: "AIM Certified Partner", img: aimLogo },
              { label: "500+ Schools", icon: School },
              { label: "Pan India", img: indiaLogo },
            ].map((item) => (
              <span
                key={item.label}
                className="animate-border-glow text-[0.95rem] md:text-[0.9rem] font-medium tracking-wide flex items-center justify-center gap-3 md:gap-4 bg-white/80 px-5 py-2.5 md:px-6 md:py-3 rounded-full border border-sky-200 backdrop-blur-sm"
                style={{ color: "#64748b" }}
              >
                {item.img ? (
                  <img src={item.img} alt={item.label} className="w-6 h-6 md:w-8 md:h-8 object-contain" />
                ) : (
                  <item.icon className="w-6 h-6 md:w-8 md:h-8 text-sky-500" />
                )}
                {item.label}
              </span>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(16px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes borderGlow {
            0%, 100% { 
              box-shadow: 0 0 4px rgba(56, 189, 248, 0.2); 
              border-color: rgba(186, 230, 253, 0.6); 
            }
            50% { 
              box-shadow: 0 0 18px rgba(56, 189, 248, 0.9), inset 0 0 8px rgba(56, 189, 248, 0.3); 
              border-color: rgba(56, 189, 248, 1); 
            }
          }
          .animate-border-glow {
            animation: borderGlow 2.5s infinite ease-in-out;
          }
        `}</style>
      </div>
    </>
  );
}